/**
 * QA Perspective Context
 *
 * SECURITY CRITICAL: This context provides UI-ONLY perspective switching for QA testing.
 *
 * GUARANTEES:
 * - Perspective NEVER affects backend authorization
 * - Only admins can activate perspective
 * - Perspective resets on logout
 * - All perspective changes are logged
 * - Mutations are guarded automatically
 *
 * HOW IT WORKS:
 * 1. Admin activates perspective (e.g., "household_member")
 * 2. UI components use effectiveRole for rendering decisions
 * 3. All mutations check canMutate() before executing
 * 4. Backend continues using actual authenticated role
 * 5. Perspective label shown at all times when active
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useQASession } from '@/contexts/QASessionContext'
import type { ActualRole, QAPerspective, QAPerspectiveState } from '@/types/qa-perspective'
import { canMutate, getMutationBlockedReason } from '@admin/lib/qa/perspective-guards'
import { isAdminRole, isOrganizationOwnerRole } from '@/lib/account/account-roles'

const PERSPECTIVE_STORAGE_KEY = 'qa_perspective_state'

interface QAPerspectiveContextType {
  // Current perspective (null = using actual role)
  perspective: QAPerspective

  // The user's actual authenticated role (NEVER changes)
  actualRole: ActualRole

  // The effective role for UI rendering (perspective ?? actualRole)
  effectiveRole: ActualRole

  // Whether perspective is currently active
  isActive: boolean

  // Set perspective (admin-only, logs event)
  setPerspective: (perspective: QAPerspective) => void

  // Reset perspective to null
  resetPerspective: () => void

  // Check if mutation is allowed
  canMutate: (requiredRole: ActualRole) => boolean

  // Get reason why mutation is blocked (null if allowed)
  getMutationBlockedReason: (requiredRole: ActualRole) => string | null
}

const QAPerspectiveContext = createContext<QAPerspectiveContextType | undefined>(undefined)

export const useQAPerspective = () => {
  const context = useContext(QAPerspectiveContext)
  if (!context) {
    throw new Error('useQAPerspective must be used within QAPerspectiveProvider')
  }
  return context
}

/**
 * Hook for getting effective role - simplified API
 * Returns the role that should be used for UI rendering decisions
 */
export const useEffectiveRole = (): ActualRole => {
  const { effectiveRole } = useQAPerspective()
  return effectiveRole
}

interface QAPerspectiveProviderProps {
  children: ReactNode
}

export const QAPerspectiveProvider: React.FC<QAPerspectiveProviderProps> = ({ children }) => {
  const { accountData } = useAuth()
  const qaSession = useQASession()
  const [perspective, setPerspectiveState] = useState<QAPerspective>(null)

  // Determine actual role from account data (using new role-based system)
  const actualRole: ActualRole = React.useMemo(() => {
    if (isAdminRole(accountData?.role)) return 'admin'
    if (isOrganizationOwnerRole(accountData?.role)) return 'organization_owner'
    if (accountData?.role === 'organization_member') return 'organization_member'
    return 'individual'
  }, [accountData?.role])

  // Calculate effective role: perspective ?? actualRole
  const effectiveRole: ActualRole = perspective ?? actualRole

  // Whether perspective is active (non-null)
  const isActive = perspective !== null

  // Load perspective from sessionStorage on mount (admin only)
  useEffect(() => {
    if (!accountData?.is_admin) {
      setPerspectiveState(null)
      sessionStorage.removeItem(PERSPECTIVE_STORAGE_KEY)
      return
    }

    try {
      const stored = sessionStorage.getItem(PERSPECTIVE_STORAGE_KEY)
      if (stored) {
        const state: QAPerspectiveState = JSON.parse(stored)
        setPerspectiveState(state.perspective)
      }
    } catch (error) {
      console.error('[QAPerspective] Failed to load perspective from storage:', error)
      sessionStorage.removeItem(PERSPECTIVE_STORAGE_KEY)
    }
  }, [accountData?.is_admin])

  // Reset perspective on logout (when accountData becomes null)
  useEffect(() => {
    if (!accountData) {
      setPerspectiveState(null)
      sessionStorage.removeItem(PERSPECTIVE_STORAGE_KEY)
    }
  }, [accountData])

  // Set perspective (admin-only)
  const setPerspective = useCallback(
    (newPerspective: QAPerspective) => {
      // Only admins can set perspective
      if (!accountData?.is_admin) {
        console.error('[QAPerspective] Non-admin attempted to set perspective')
        return
      }

      const previousPerspective = perspective

      // Update state
      setPerspectiveState(newPerspective)

      // Persist to sessionStorage
      if (newPerspective !== null) {
        const state: QAPerspectiveState = {
          perspective: newPerspective,
          activatedAt: new Date().toISOString(),
          sessionId: qaSession.session?.metadata.sessionId || crypto.randomUUID(),
        }
        sessionStorage.setItem(PERSPECTIVE_STORAGE_KEY, JSON.stringify(state))
      } else {
        sessionStorage.removeItem(PERSPECTIVE_STORAGE_KEY)
      }

      // Log event to QA session if active
      if (qaSession.session) {
        qaSession.captureEvent(
          'manual_note',
          `Perspective changed from ${String(previousPerspective)} to ${String(newPerspective)}`,
          {
            from: previousPerspective,
            to: newPerspective,
            adminAccountId: accountData.id,
          },
        )
      }

      console.log('[QAPerspective] Perspective changed:', {
        from: previousPerspective,
        to: newPerspective,
        actualRole,
        effectiveRole: newPerspective ?? actualRole,
      })
    },
    [perspective, accountData, qaSession, actualRole],
  )

  // Reset perspective
  const resetPerspective = useCallback(() => {
    setPerspective(null)
  }, [setPerspective])

  // Check if mutation is allowed
  const canMutateRole = useCallback(
    (requiredRole: ActualRole): boolean => {
      return canMutate(requiredRole, perspective, actualRole)
    },
    [perspective, actualRole],
  )

  // Get mutation blocked reason
  const getMutationBlockedReasonForRole = useCallback(
    (requiredRole: ActualRole): string | null => {
      return getMutationBlockedReason(requiredRole, perspective, actualRole)
    },
    [perspective, actualRole],
  )

  const value: QAPerspectiveContextType = {
    perspective,
    actualRole,
    effectiveRole,
    isActive,
    setPerspective,
    resetPerspective,
    canMutate: canMutateRole,
    getMutationBlockedReason: getMutationBlockedReasonForRole,
  }

  return <QAPerspectiveContext.Provider value={value}>{children}</QAPerspectiveContext.Provider>
}
